import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronDown, ChevronUp, Clock, User, X } from 'lucide-react';
import {
  type CollectionProgressSelection,
  fetchCollectionProgress,
  type CollectionProgressFilters,
  type CollectionProgressResponse,
  type CollectionStatusKey,
  type CollectionStatusMatrix,
  type StatusAllocationMetric,
  type WorklistCounts,
  type WorklistStatus,
} from '@/integrations/api/services/collectionProgressService';

const POLL_MS = 60_000;
const IDLE_MS = 5 * 60_000;

interface CollectionProgressPanelProps {
  filters: CollectionProgressFilters;
  /** True while application details covers the dashboard. Polling stays off. */
  paused?: boolean;
  selectedPtpWorklist?: WorklistStatus | null;
  onPtpWorklistSelect?: (status: WorklistStatus) => void;
  selectedProgressFilter?: CollectionProgressSelection | null;
  onProgressFilterSelect?: (selection: CollectionProgressSelection | null) => void;
}

type DisplayMode = 'count' | 'amount';

const STATUS_COLUMNS: Array<{
  key: CollectionStatusKey;
  label: string;
  dot: string;
  percentage: string;
  pill: string;
}> = [
  { key: 'overdue', label: 'OVERDUE', dot: 'bg-red-500', percentage: 'text-red-700', pill: 'bg-red-100 text-red-700' },
  { key: 'partially_paid', label: 'PARTIALLY PAID', dot: 'bg-orange-300', percentage: 'text-orange-700', pill: 'bg-orange-100 text-orange-800' },
  { key: 'paid', label: 'PAID', dot: 'bg-green-600', percentage: 'text-green-700', pill: 'bg-green-100 text-green-800' },
];

const STATUS_LABELS = Object.fromEntries(
  STATUS_COLUMNS.map((column) => [column.key, column.label]),
) as Record<CollectionStatusKey, string>;

function sameSelection(
  left: CollectionProgressSelection | null | undefined,
  right: CollectionProgressSelection,
) {
  return Boolean(left)
    && left?.status === right.status
    && left?.bucket === right.bucket;
}

function formatCount(value: number) {
  return Math.round(value).toLocaleString('en-IN');
}

function formatAllocationAmount(value: number) {
  if (!value) return '—';
  if (Math.abs(value) >= 1_00_00_000) return `₹${(value / 1_00_00_000).toFixed(1)}Cr`;
  if (Math.abs(value) >= 1_00_000) return `₹${(value / 1_00_000).toFixed(1)}L`;
  return `₹${Math.round(value).toLocaleString('en-IN')}`;
}

function metricValue(metric: StatusAllocationMetric, mode: DisplayMode, emptyAsDash = false) {
  if (mode === 'amount') return formatAllocationAmount(metric.amount);
  if (emptyAsDash && metric.count === 0) return '—';
  return formatCount(metric.count);
}

function metricPercentage(metric: StatusAllocationMetric, mode: DisplayMode) {
  const value = mode === 'amount' ? metric.amount_percentage : metric.count_percentage;
  return `${Number(value || 0).toFixed(1)}%`;
}

function WorklistChip({
  label,
  count,
  variant,
  icon: Icon,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  variant: 'today' | 'missed' | 'neutral';
  icon: typeof Calendar;
  selected: boolean;
  onClick?: () => void;
}) {
  const idle =
    'border-gray-200 bg-white shadow-sm hover:border-gray-300 hover:bg-gray-50 hover:shadow-md';

  const active =
    variant === 'missed'
      ? 'border-red-300 bg-red-50 shadow-[0_0_0_3px_rgba(248,113,113,0.16)]'
      : 'border-emerald-400 bg-emerald-50 shadow-[0_0_0_3px_rgba(34,181,94,0.16)]';

  const iconWrap =
    variant === 'today'
      ? 'bg-gray-100 text-emerald-700'
      : variant === 'missed'
        ? 'bg-gray-100 text-red-600'
        : 'bg-gray-100 text-gray-500';

  const countColor =
    variant === 'today' ? 'text-emerald-700' : variant === 'missed' ? 'text-red-600' : 'text-gray-900';

  const labelColor = selected
    ? variant === 'missed'
      ? 'text-red-700'
      : 'text-emerald-800'
    : 'text-gray-500';

  const interactive = Boolean(onClick);
  const Tag = interactive ? 'button' : 'div';

  return (
    <Tag
      {...(interactive
        ? {
            type: 'button' as const,
            onClick,
            'aria-pressed': selected,
            'aria-label': `${selected ? 'Remove' : 'Filter by'} ${label}`,
          }
        : {})}
      className={`relative flex w-full min-w-0 items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left ${
        interactive
          ? `cursor-pointer transition-all duration-150 hover:-translate-y-px active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
              variant === 'missed'
                ? 'focus-visible:ring-red-400'
                : 'focus-visible:ring-[#22B55E]'
            } ${selected ? active : idle}`
          : idle
      }`}
    >
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconWrap}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className={`block text-[11px] font-semibold uppercase tracking-wide ${labelColor}`}>
          {label}
        </span>
        <span className={`block text-lg font-bold leading-tight tabular-nums ${countColor}`}>{count}</span>
      </span>
    </Tag>
  );
}

const WORKLIST_CARDS: Array<{
  label: string;
  status: WorklistStatus;
  variant: 'today' | 'missed' | 'neutral';
  icon: typeof Calendar;
}> = [
  { label: 'Today', status: 'today', variant: 'today', icon: Calendar },
  { label: 'Missed', status: 'missed', variant: 'missed', icon: Clock },
  { label: 'Not Set', status: 'not_set', variant: 'neutral', icon: User },
  { label: 'Later', status: 'later', variant: 'neutral', icon: Clock },
];

function WorklistGroup({
  title,
  counts,
  selectedStatus,
  onSelect,
}: {
  title: string;
  counts: WorklistCounts;
  selectedStatus?: WorklistStatus | null;
  onSelect?: (status: WorklistStatus) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
      <div className="flex shrink-0 items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
          <Calendar className="h-4 w-4" />
        </span>
        <div className="leading-tight">
          <h2 className="text-sm font-bold tracking-wide text-gray-900 uppercase">Daily Worklist</h2>
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{title}</p>
        </div>
      </div>
      <div className="grid min-w-0 flex-1 grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        {WORKLIST_CARDS.map((card) => (
          <WorklistChip
            key={card.status}
            label={card.label}
            count={counts[card.status]}
            variant={card.variant}
            icon={card.icon}
            selected={Boolean(onSelect) && selectedStatus === card.status}
            onClick={onSelect ? () => onSelect(card.status) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

export default function CollectionProgressPanel({
  filters,
  paused = false,
  selectedPtpWorklist = null,
  onPtpWorklistSelect,
  selectedProgressFilter = null,
  onProgressFilterSelect,
}: CollectionProgressPanelProps) {
  const [data, setData] = useState<CollectionProgressResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [displayMode, setDisplayMode] = useState<DisplayMode>('count');
  const lastActiveAt = useRef(Date.now());
  const pollId = useRef<number | null>(null);
  const pausedRef = useRef(paused);
  const filtersKeyRef = useRef('');
  const wasPaused = useRef(false);
  pausedRef.current = paused;

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        emi_month: filters.emi_month || undefined,
        state: filters.state ?? [],
        branch: filters.branch ?? [],
        tl_name: filters.tl_name ?? [],
        rm_name: filters.rm_name ?? [],
        source_tl_name: filters.source_tl_name ?? [],
        source_rm_name: filters.source_rm_name ?? [],
        dealer: filters.dealer ?? [],
        lender: filters.lender ?? [],
        search: filters.search || undefined,
        loan_type: filters.loan_type ?? [],
        status: filters.status ?? [],
        dpd_bucket: filters.dpd_bucket ?? [],
        nach_status: filters.nach_status ?? [],
        ptp_date_filter: filters.ptp_date_filter ?? [],
        ptp_worklist: filters.ptp_worklist || undefined,
        demand_num: filters.demand_num ?? [],
        special_case_filter: filters.special_case_filter ?? [],
      }),
    [filters.emi_month, filters.state, filters.branch, filters.tl_name, filters.rm_name, filters.source_tl_name, filters.source_rm_name, filters.dealer, filters.lender, filters.search, filters.loan_type, filters.status, filters.dpd_bucket, filters.nach_status, filters.ptp_date_filter, filters.ptp_worklist, filters.demand_num, filters.special_case_filter],
  );
  filtersKeyRef.current = filtersKey;

  const stopPoll = () => {
    if (pollId.current != null) {
      window.clearInterval(pollId.current);
      pollId.current = null;
    }
  };

  const quietRefresh = () => {
    fetchCollectionProgress(JSON.parse(filtersKeyRef.current))
      .then((res) => setData(res))
      .catch((err) => {
        console.error('Collection progress refresh failed:', err);
      });
  };

  const startPoll = () => {
    stopPoll();
    pollId.current = window.setInterval(() => {
      if (pausedRef.current || document.visibilityState === 'hidden') {
        stopPoll();
        return;
      }
      if (Date.now() - lastActiveAt.current >= IDLE_MS) {
        stopPoll();
        return;
      }
      quietRefresh();
    }, POLL_MS);
  };

  useEffect(() => {
    const markActive = () => {
      const idleFor = Date.now() - lastActiveAt.current;
      lastActiveAt.current = Date.now();
      if (pausedRef.current || document.visibilityState === 'hidden') return;
      if (idleFor >= IDLE_MS) {
        quietRefresh();
        startPoll();
      }
    };

    window.addEventListener('mousemove', markActive, { passive: true });
    window.addEventListener('click', markActive);
    window.addEventListener('keydown', markActive);
    window.addEventListener('scroll', markActive, { passive: true, capture: true });

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stopPoll();
        return;
      }
      lastActiveAt.current = Date.now();
      if (!pausedRef.current) {
        quietRefresh();
        startPoll();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('mousemove', markActive);
      window.removeEventListener('click', markActive);
      window.removeEventListener('keydown', markActive);
      window.removeEventListener('scroll', markActive, true);
      document.removeEventListener('visibilitychange', onVisibility);
      stopPoll();
    };
  }, []);

  useEffect(() => {
    if (paused) {
      stopPoll();
      wasPaused.current = true;
      return;
    }
    lastActiveAt.current = Date.now();
    if (wasPaused.current) {
      quietRefresh();
      wasPaused.current = false;
    }
    startPoll();
    return () => stopPoll();
  }, [paused, filtersKey]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    fetchCollectionProgress(JSON.parse(filtersKey))
      .then((res) => {
        if (!active) return;
        setData(res);
      })
      .catch((err) => {
        if (!active) return;
        console.error('Collection progress fetch failed:', err);
        setError('Could not load collection progress');
        setData(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [filtersKey]);

  if (loading && !data) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm animate-pulse">
        <div className="h-5 w-52 bg-gray-200 rounded mb-3" />
        <div className="h-2 w-full bg-gray-100 rounded mb-5" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        {error}
      </div>
    );
  }

  if (!data) return null;

  const { status_summary, status_buckets, daily_worklist } = data;
  const choose = (selection: CollectionProgressSelection) => {
    onProgressFilterSelect?.(
      sameSelection(selectedProgressFilter, selection) ? null : selection,
    );
  };
  const selectedMatrix: CollectionStatusMatrix = selectedProgressFilter?.bucket
    ? status_buckets.find((row) => row.bucket === selectedProgressFilter.bucket)
      || status_summary
    : status_summary;
  const selectedMetric = selectedProgressFilter?.status
    ? selectedMatrix[selectedProgressFilter.status]
    : selectedMatrix.allocation;
  const selectedLabel = selectedProgressFilter
    ? [
        selectedProgressFilter.bucket,
        selectedProgressFilter.status
          ? STATUS_LABELS[selectedProgressFilter.status]
          : null,
      ].filter(Boolean).join(' · ') || 'allocated'
    : '';
  const allocationTile: {
    key: 'allocation';
    label: string;
    dot: string;
    percentage: string;
    pill: string;
  } = {
    key: 'allocation',
    label: 'ALLOCATION',
    dot: 'bg-slate-900',
    percentage: 'text-gray-600',
    pill: 'bg-gray-200 text-gray-700',
  };

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="space-y-3 p-3 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-0.5">
              {(['count', 'amount'] as DisplayMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setDisplayMode(mode)}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    displayMode === mode
                      ? 'bg-white text-gray-950'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {mode === 'count' ? 'Count' : 'Amount'}
                </button>
              ))}
            </div>
            {selectedProgressFilter && (
              <div className="flex items-center gap-2 rounded-full bg-[#061C23] py-1 pl-3 pr-1.5 text-xs font-medium text-white">
                <span>
                  Showing {formatCount(selectedMetric.count)} {selectedLabel} applications
                </span>
                <button
                  type="button"
                  onClick={() => onProgressFilterSelect?.(null)}
                  className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15 hover:bg-white/25"
                  aria-label="Clear collection progress filter"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>

          <div className="overflow-x-auto pb-1">
            <div className="grid min-w-[640px] grid-cols-4 gap-2.5">
              {[...STATUS_COLUMNS, allocationTile].map((column) => {
                const selection: CollectionProgressSelection =
                  column.key === 'allocation' ? {} : { status: column.key };
                const selected = sameSelection(selectedProgressFilter, selection);
                const metric = status_summary[column.key];
                return (
                  <button
                    key={column.key}
                    type="button"
                    onClick={() => choose(selection)}
                    className={`flex min-w-0 flex-col gap-2 rounded-xl border px-3 py-3 text-left transition-all duration-150 ease-out hover:-translate-y-px hover:border-gray-400 hover:bg-gray-50 active:scale-[0.98] ${
                      column.key === 'allocation' ? 'bg-gray-100' : 'bg-white'
                    } ${selected ? 'border-2 border-[#061C23]' : 'border-gray-200'}`}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${column.dot}`} />
                      <span className="truncate text-[10px] font-semibold tracking-wider text-gray-600">
                        {column.label}
                      </span>
                    </span>
                    <span className="text-xl font-bold tabular-nums text-gray-950">
                      {metricValue(metric, displayMode)}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${column.pill}`}>
                        {metricPercentage(metric, displayMode)}
                      </span>
                      <span className="text-xs text-gray-500">
                        {displayMode === 'count' ? 'of cases' : 'of allocation'}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setDetailsOpen((open) => !open)}
          className="flex w-full items-center justify-center gap-1.5 border-t border-gray-200 bg-gray-100 px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-200"
        >
          {detailsOpen ? 'Hide Bucket-wise Details' : 'Show Bucket-wise Details'}
          {detailsOpen
            ? <ChevronUp className="h-4 w-4" />
            : <ChevronDown className="h-4 w-4" />}
        </button>

        {detailsOpen && (
          <div className="overflow-x-auto border-t border-gray-200">
            <table className="w-full min-w-[940px] text-sm">
              <thead>
                <tr className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 text-left">DPD Bucket</th>
                  {STATUS_COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      className={`px-4 py-3 text-right ${
                        selectedProgressFilter?.status === column.key ? 'bg-gray-200' : ''
                      }`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`h-1.5 w-1.5 rounded-full ${column.dot}`} />
                        {column.label}
                      </span>
                    </th>
                  ))}
                  <th className="bg-gray-100 px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-900" />
                      Allocation
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {status_buckets.map((row) => (
                  <tr key={row.bucket} className="border-t border-gray-200">
                    <td className="px-4 py-4 font-medium text-gray-950">{row.bucket}</td>
                    {STATUS_COLUMNS.map((column) => {
                      const selection = { bucket: row.bucket, status: column.key };
                      const selected = sameSelection(selectedProgressFilter, selection);
                      const metric = row[column.key];
                      return (
                        <td
                          key={column.key}
                          className={`p-0 text-right ${
                            selectedProgressFilter?.status === column.key ? 'bg-gray-100' : ''
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => choose(selection)}
                            className={`flex w-full flex-col items-end gap-1 px-4 py-4 hover:bg-gray-200 ${
                              selected ? 'ring-2 ring-inset ring-[#061C23]' : ''
                            }`}
                          >
                            <span className="font-semibold tabular-nums text-gray-950">
                              {metricValue(metric, displayMode, true)}
                            </span>
                            <span className={`text-xs ${column.percentage}`}>
                              {metric.count ? metricPercentage(metric, displayMode) : '—'}
                            </span>
                          </button>
                        </td>
                      );
                    })}
                    <td className="bg-gray-100 p-0 text-right">
                      <button
                        type="button"
                        onClick={() => choose({ bucket: row.bucket })}
                        className={`flex w-full flex-col items-end gap-1 px-4 py-4 hover:bg-gray-200 ${
                          sameSelection(selectedProgressFilter, { bucket: row.bucket })
                            ? 'ring-2 ring-inset ring-[#061C23]'
                            : ''
                        }`}
                      >
                        <span className="font-bold tabular-nums text-gray-950">
                          {metricValue(row.allocation, displayMode, true)}
                        </span>
                        <span className="text-xs text-gray-500">100.0%</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
        <WorklistGroup
          title="PTP"
          counts={daily_worklist.ptp}
          selectedStatus={selectedPtpWorklist}
          onSelect={onPtpWorklistSelect}
        />
      </div>
    </div>
  );
}
